import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth';

const REFRESH = 120_000;

/** Ein Nav-Badge mit einer oder mehreren Zahlen (z. B. NEU / Gesamt). */
export interface NavBadge {
  /** Zahlen von links nach rechts — die letzte Zahl ist immer „Gesamt". */
  parts: number[];
  /** Beschriftung je Zahl (für den Tooltip), gleiche Länge wie `parts`. */
  labels: string[];
}

/** Mitternacht heute (lokal) als ms-Timestamp — Grenze für „seit 0:00". */
function todayStartMs(): number {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Datum (ISO-String oder Unix-ms-String, wie Weclapp es liefert) → ms oder null. */
function parseDateMs(value?: string | null): number | null {
  if (!value) return null;
  const num = Number(value);
  if (!Number.isNaN(num) && num > 0) return num; // Unix-ms (Weclapp createdDate)
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** Zählt Einträge mit Erstell-Zeitstempel ≥ Mitternacht heute. */
function countNewToday<T>(
  items: T[] | undefined,
  getDate: (it: T) => string | null | undefined,
): number {
  if (!items?.length) return 0;
  const start = todayStartMs();
  let n = 0;
  for (const it of items) {
    const ms = parseDateMs(getDate(it));
    if (ms != null && ms >= start) n++;
  }
  return n;
}

/**
 * Liefert Badges für Triage-/Worklist-Menüpunkte, gekeyt nach Pfad.
 * Format: NEU (seit 0:00) / Gesamt — bei Tickets dreiteilig NEU / nicht zugewiesen / gesamt.
 * Admin-only Quellen werden nur für Admins abgefragt.
 */
export function useNavBadges(): Record<string, NavBadge> {
  const isAdmin = useAuthStore((s) => s.user?.role === 'ADMIN');
  const weclappUserId = useAuthStore((s) => s.user?.weclappUserId);

  const lowStock = useQuery({
    queryKey: ['nav-badge', 'low-stock'],
    queryFn: () => api.lagerGetLowStockCount(),
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  const suggestions = useQuery({
    queryKey: ['nav-badge', 'contact-suggestions'],
    queryFn: () => api.getContactSuggestions({ status: 'PENDING', limit: 200 }),
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  // Rückrufe statt unbekannter Nummern: die Anrufer-Seite öffnet mit der
  // Rückrufliste, und offene Rückrufe sind das, was tatsächlich liegen bleibt.
  // Bewusst für alle Rollen — Rückrufe sind Techniker-Tagesgeschäft, nicht Admin-Sache.
  const callbacks = useQuery({
    queryKey: ['nav-badge', 'callbacks'],
    queryFn: () => api.getCallbacksStats(),
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  // Chat-Inbox: ungelesene Konversationen sind das, was liegen bleiben würde.
  const chats = useQuery({
    queryKey: ['chats-stats'],
    queryFn: () => api.getChatsStats(),
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  const senderTriage = useQuery({
    queryKey: ['nav-badge', 'ticket-sender-triage'],
    queryFn: () => api.getTicketSenderTriage('OPEN', 200),
    enabled: isAdmin,
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  // Heimatadress-Änderungen warten auf Bestätigung — sie liegen sonst unsichtbar
  // in der Benutzerverwaltung, während die KM-Abrechnung mit dem alten Stand rechnet.
  const homeAddressChanges = useQuery({
    queryKey: ['nav-badge', 'home-address-requests'],
    queryFn: () => api.getHomeAddressRequests('PENDING'),
    enabled: isAdmin,
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  // Offene Materialanfragen. Seit interne Anfragen nicht mehr automatisch genehmigt
  // werden, ist das die einzige Stelle, an der sie ohne Push auffallen.
  const materialRequests = useQuery({
    queryKey: ['nav-badge', 'technician-requests'],
    queryFn: () => api.getAdminTechnicianRequests('PENDING'),
    enabled: isAdmin,
    refetchInterval: REFRESH,
    staleTime: REFRESH,
  });

  // Teilt den Cache mit der Tickets-Seite (identischer queryKey) → kein zusätzlicher Weclapp-Call.
  const myTickets = useQuery({
    queryKey: ['my-weclapp-tickets'],
    queryFn: () => api.getMyWeclappTickets(),
    enabled: !!weclappUserId,
    staleTime: 30_000,
    refetchInterval: REFRESH,
  });

  const badges: Record<string, NavBadge> = {};

  // Unterbestand — einzelne Zahl (kein NEU/Gesamt sinnvoll)
  if (lowStock.data?.count) {
    badges['/inventory/low-stock'] = { parts: [lowStock.data.count], labels: ['Unterbestand'] };
  }

  // AP-Vorschläge — NEU / Gesamt
  if (suggestions.data) {
    const total = suggestions.data.total ?? suggestions.data.suggestions.length;
    if (total > 0) {
      const neu = countNewToday(suggestions.data.suggestions, (s) => s.createdAt);
      badges['/contact-suggestions'] = { parts: [neu, total], labels: ['neu seit 0:00', 'gesamt'] };
    }
  }

  // Anrufer — überfällig / offene Rückrufe
  if (callbacks.data) {
    const offen = callbacks.data.open + callbacks.data.inProgress;
    if (offen > 0) {
      badges['/admin/callers'] = {
        parts: [callbacks.data.overdue, offen],
        labels: ['überfällige Wiedervorlage', 'offene Rückrufe'],
      };
    }
  }

  // Chat-Inbox — ungelesen / offen
  if (chats.data && chats.data.open > 0) {
    badges['/chats'] = {
      parts: [chats.data.withUnread, chats.data.open],
      labels: ['mit ungelesenen Nachrichten', 'offene Chats'],
    };
  }

  // Tickets ohne Kunde — NEU / Gesamt
  if (senderTriage.data?.items?.length) {
    const items = senderTriage.data.items;
    const neu = countNewToday(items, (t) => t.createdAt);
    badges['/admin/ticket-sender-triage'] = {
      parts: [neu, items.length],
      labels: ['neu seit 0:00', 'gesamt'],
    };
  }

  // Heimatadressen zur Bestätigung — NEU / Gesamt
  if (homeAddressChanges.data?.requests?.length) {
    const items = homeAddressChanges.data.requests;
    const neu = countNewToday(items, (r) => r.createdAt);
    badges['/admin/users'] = { parts: [neu, items.length], labels: ['neu seit 0:00', 'gesamt'] };
  }

  // Materialanfragen zur Freigabe — NEU / Gesamt
  if (materialRequests.data?.requests?.length) {
    const items = materialRequests.data.requests;
    const neu = countNewToday(items, (r) => r.createdAt);
    badges['/admin/technician-requests'] = {
      parts: [neu, items.length],
      labels: ['neu seit 0:00', 'gesamt'],
    };
  }

  // Tickets — NEU / nicht zugewiesen / gesamt (global: ALLE nicht geschlossenen Tickets)
  if (myTickets.data) {
    const stats = myTickets.data.openStats;
    if (stats && stats.total > 0) {
      // Globale Zahlen aus der Server-Snapshot (alle offenen Tickets).
      badges['/tickets'] = {
        parts: [stats.newToday, stats.unassigned, stats.total],
        labels: ['neu seit 0:00', 'nicht zugewiesen', 'gesamt'],
      };
    } else if (!stats) {
      // Snapshot noch kalt → Fallback auf eigene + offener Pool (disjunkt, einfach addierbar).
      const assigned = myTickets.data.tickets ?? [];
      const unassigned = myTickets.data.unassignedRecent ?? [];
      const total = assigned.length + unassigned.length;
      if (total > 0) {
        const neu =
          countNewToday(assigned, (t) => t.createdDate) +
          countNewToday(unassigned, (t) => t.createdDate);
        badges['/tickets'] = {
          parts: [neu, unassigned.length, total],
          labels: ['neu seit 0:00', 'nicht zugewiesen', 'gesamt'],
        };
      }
    }
  }

  return badges;
}
